import React, { type RefObject, useCallback } from 'react';

import Menu, { MenuItem } from '@amzn/meridian/menu';
import { COUNTRY_ID_MAPPING } from 'src/config/CountryIdMapping';
import useOpenCloseState from 'src/hooks/useOpenCloseState';
import JmrService from 'src/services/jmr/jmrService';
import type {
  PlacementDetailsAllocatedJobShift,
  PlacementDetailsNonPhysicalLimitation,
} from 'src/store/placementDetailsPageSlice/types';
import { createFileUrl, downloadFile } from 'src/utils/fileUtils';

import OomdModal from './oomdModal/oomdModal';
import TwpaModal from './twpaModal/twpaModal';

interface PlacementExportMenuProps {
  placementId: string;
  anchorRef: RefObject<HTMLButtonElement>;
  isMenuOpen: boolean;
  closeMenu: VoidFunction;
  newestJmrFileName: string;
  countryId: number;
  allocatedJobShifts: PlacementDetailsAllocatedJobShift[];
  nonPhysicalLimitations?: PlacementDetailsNonPhysicalLimitation[];
  jobModificationsEnabled?: boolean;
  nonPhysicalLimitationsEnabled?: boolean;
}

const PlacementExportMenu = ({
  placementId,
  anchorRef,
  isMenuOpen,
  closeMenu,
  newestJmrFileName,
  countryId,
  allocatedJobShifts,
  nonPhysicalLimitations = [],
  jobModificationsEnabled = false,
  nonPhysicalLimitationsEnabled = false,
}: PlacementExportMenuProps) => {
  const { isOpen: isTwpaModalOpen, open: openTwpaModal, close: closeTwpaModal } = useOpenCloseState();
  const { isOpen: isOomdModalOpen, open: openOomdModal, close: closeOomdModal } = useOpenCloseState();

  const downloadJmr = useCallback(async () => {
    const { data } = await JmrService.downloadJMR(newestJmrFileName);
    if (data) {
      downloadFile(createFileUrl(data, 'application/pdf'), 'jmr-report.pdf');
    }
  }, [newestJmrFileName]);

  return (
    <>
      <Menu anchorNode={anchorRef.current ?? undefined} open={isMenuOpen} position="bottom" onClose={closeMenu}>
        {newestJmrFileName && <MenuItem onClick={downloadJmr}>Export JMR</MenuItem>}
        {countryId === COUNTRY_ID_MAPPING.CANADA_COUNTRY_ID ? (
          <MenuItem onClick={openOomdModal}>OOMD PDF</MenuItem>
        ) : (
          <MenuItem onClick={openTwpaModal}>TWPA PDF</MenuItem>
        )}
      </Menu>
      <OomdModal placementId={Number(placementId)} isOomdModalOpen={isOomdModalOpen} closeModal={closeOomdModal} />
      <TwpaModal
        placementId={Number(placementId)}
        isTwpaModalOpen={isTwpaModalOpen}
        closeTwpaModal={closeTwpaModal}
        allocatedJobShifts={allocatedJobShifts}
        nonPhysicalLimitations={nonPhysicalLimitations}
        isJobModificationsEnabledForSite={jobModificationsEnabled}
        isNonPhysicalLimitationsEnabledForSite={nonPhysicalLimitationsEnabled}
      />
    </>
  );
};

export default PlacementExportMenu;
